import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { uniqueId } from 'lodash-es'
import { observer } from 'mobx-react'

import { getExternalIdCustomName } from 'Modules/order/utils/order.utils'

import { CardDetailsList } from 'Components/CardDetailsList/CardDetailsList'
import type { CardDetailsListItem } from 'Components/CardDetailsList/CardDetailsList.types'
import { ContentCard } from 'Components/ContentCard/ContentCard'
import { FormatDate } from 'Components/FormatDate/FormatDate'
import { OrderTagsRow } from 'Components/Tagging/OrderTagsRow'
import { useTaggingEnabled } from 'Components/Tagging/useTaggingEnabled'

import { useStore } from 'Stores/index'

import { useActiveUser, useIsOrganisation } from 'Hooks'

import { dashOrSpaceToUnderscore, getFullContactName } from 'Utilities'

import { CaseItemType } from 'Portal/__generated__/globalTypes'

import { OrderIntercomButton } from '../OrderIntercomButton/OrderIntercomButton'
import type { OrderDetailsCardProps } from './OrderDetailsCard.types'

export const OrderDetailsCard = observer((props: OrderDetailsCardProps) => {
  const { caseItem, refetch, title } = props
  const intl = useIntl()

  const isOrganisation = useIsOrganisation()
  const { portalConfigStore, organisationStore, portalSettingsStore } =
    useStore()
  const { portalConfig } = portalConfigStore
  const { organisationConfigList } = organisationStore
  const { isAppReadOnly } = portalSettingsStore
  const { activeOrganisation } = useActiveUser()
  const isTaggingEnabled = useTaggingEnabled()
  const showTags = !!activeOrganisation?.id && isTaggingEnabled

  const externalIdCustomName = getExternalIdCustomName(
    organisationConfigList,
    intl,
  )

  const filteredEmployeesList = useMemo(
    () =>
      caseItem.data.requestedPhonePlan?.employees?.filter(
        employeeName => employeeName,
      ),
    [caseItem],
  )

  const employeesLabel = useMemo(
    () =>
      filteredEmployeesList?.length === 1
        ? 'PhonePlanListContent_header__employee'
        : 'Employees',
    [filteredEmployeesList],
  )

  const employeesValue = useMemo(
    () =>
      filteredEmployeesList?.length ? (
        <ul className="CardDetailsList-employees-list">
          {filteredEmployeesList.map(employeeName => (
            <li key={uniqueId()}>{employeeName}</li>
          ))}
        </ul>
      ) : undefined,
    [filteredEmployeesList],
  )

  const isLostDevice =
    dashOrSpaceToUnderscore(caseItem.caseType) === CaseItemType.lost_device &&
    typeof caseItem?.data.isReplacementRequested === 'boolean'

  const generateDetailsCardListItems = useMemo(
    (): CardDetailsListItem[] => [
      {
        label: 'RequestDetails_request_id',
        value: caseItem.id,
      },
      ...(isOrganisation && portalConfig?.poRcnInOrderSummary
        ? [
            {
              label: 'RequestDetails_po_rcn',
              value: caseItem.poRcn,
            },
          ]
        : []),

      ...(isOrganisation && portalConfig?.customClientReferenceInOrderSummary
        ? [
            {
              label: 'RequestDetails_custom_client_reference',
              value: caseItem?.customClientReference,
            },
          ]
        : []),

      ...(caseItem.externalCaseId || caseItem.parentCaseItem?.externalCaseId
        ? [
            {
              disableLabelTranslation: true,
              label: externalIdCustomName,
              value:
                caseItem.externalCaseId ||
                caseItem.parentCaseItem?.externalCaseId,
            },
          ]
        : []),

      ...(isOrganisation && portalConfig?.billingIdentifierInOrderSummary
        ? [
            {
              label: 'RequestDetails_billing_identifier',
              value: caseItem?.billingIdentifier,
            },
          ]
        : []),

      {
        label: 'RequestDetails_date',
        value: <FormatDate value={caseItem.created} />,
      },
      {
        label: 'RequestDetails_customerLabel',
        value: caseItem.customerLabel,
      },
      {
        label: employeesLabel,
        value: employeesValue,
      },
      {
        label: 'Phone_plan',
        value: caseItem.data.requestedPhonePlan?.name,
      },
      {
        label: 'Quantity',
        value: caseItem.data.requestedPhonePlan?.number?.toString(),
      },
      {
        label: 'Created_by',
        value: getFullContactName(caseItem.user, {
          intl,
          showDefaultText: false,
        }),
      },
      ...(isLostDevice
        ? [
            {
              label: 'Order_replacement_requested',
              value: intl.formatMessage({
                id: caseItem?.data.isReplacementRequested ? 'Yes' : 'No',
              }),
            },
          ]
        : []),
    ],
    [
      caseItem.id,
      caseItem.poRcn,
      caseItem?.customClientReference,
      caseItem.externalCaseId,
      caseItem.parentCaseItem?.externalCaseId,
      caseItem?.billingIdentifier,
      caseItem.created,
      caseItem.customerLabel,
      caseItem.data.requestedPhonePlan?.name,
      caseItem.data.requestedPhonePlan?.number,
      caseItem.data.isReplacementRequested,
      caseItem.user,
      isOrganisation,
      portalConfig?.poRcnInOrderSummary,
      portalConfig?.customClientReferenceInOrderSummary,
      portalConfig?.billingIdentifierInOrderSummary,
      externalIdCustomName,
      employeesLabel,
      employeesValue,
      isLostDevice,
      intl,
    ],
  )

  return (
    <ContentCard className="OrderDetailsCard" title={title}>
      {showTags && (
        <OrderTagsRow
          caseItem={caseItem}
          readOnly={isAppReadOnly}
          refetch={refetch}
        />
      )}

      <CardDetailsList
        className="mb-4"
        listItems={generateDetailsCardListItems}
      />

      <OrderIntercomButton caseItemId={caseItem.id} />
    </ContentCard>
  )
})
